import React, { useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../context/AuthContext';
import { KeyIcon, CheckIcon, XMarkIcon, ShieldCheckIcon } from '@heroicons/react/24/outline';
import { motion } from 'framer-motion';

const ResetPassword = () => {
  // Récupérer et décoder le token de l'URL
  const { token: encodedToken } = useParams();
  const token = decodeURIComponent(encodedToken);
  const navigate = useNavigate();
  const { api } = useContext(AuthContext);
  
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  // Validation du mot de passe
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [passwordCriteria, setPasswordCriteria] = useState({
    length: false,
    uppercase: false,
    lowercase: false,
    number: false,
    special: false
  });
  
  const handlePasswordChange = (e) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
    
    // Vérifier les critères
    const criteria = {
      length: newPassword.length >= 8,
      uppercase: /[A-Z]/.test(newPassword),
      lowercase: /[a-z]/.test(newPassword),
      number: /[0-9]/.test(newPassword),
      special: /[^A-Za-z0-9]/.test(newPassword)
    };
    
    setPasswordCriteria(criteria);
    
    // Calculer la force du mot de passe (0-4)
    const strength = Object.values(criteria).filter(Boolean).length;
    setPasswordStrength(strength);
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    
    // Validation
    if (password.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caractères');
      return;
    }
    
    if (password !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Afficher le token pour débogage
      console.log('Token utilisé pour la réinitialisation:', token);
      console.log('Longueur du token:', token.length);
      
      const response = await api.post('/users/reset-password', {
        token,
        password
      });
      
      console.log('Réponse de réinitialisation:', response.data);
      
      setSuccess(response.data.message || 'Votre mot de passe a été réinitialisé avec succès');
      
      // Rediriger vers la page de connexion après 3 secondes
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (err) {
      console.error('Erreur de réinitialisation:', err.response?.data || err.message);
      setError(err.response?.data?.message || 'Une erreur est survenue lors de la réinitialisation du mot de passe');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Fonction pour obtenir la couleur de la barre de force
  const getStrengthColor = () => {
    if (passwordStrength <= 1) return 'bg-red-500';
    if (passwordStrength === 2) return 'bg-orange-500';
    if (passwordStrength === 3) return 'bg-yellow-500';
    return 'bg-green-500';
  };
  
  return (
    <div className="min-h-screen flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative">
      {/* Fond d'image avec overlay */}
      <div 
        className="absolute inset-0 bg-cover bg-center z-0" 
        style={{ 
          backgroundImage: 'url(/images/theatre-background.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          width: '100vw',
          height: '100vh',
          position: 'fixed',
          top: 0,
          left: 0,
          filter: 'brightness(0.4)'
        }}
      ></div>
      
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full space-y-8 bg-white p-8 rounded-xl shadow-lg z-10 relative"
      >
        <div>
          <img
            className="mx-auto h-24 w-auto"
            src="/logo-noir.png"
            alt="Planning-Corporate"
          />
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            Réinitialisation du mot de passe
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            Veuillez choisir un nouveau mot de passe sécurisé
          </p>
        </div>
        
        {error && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4 rounded-md shadow-sm"
          >
            <div className="flex">
              <div className="py-1">
                <XMarkIcon className="h-6 w-6 text-red-500 mr-3" />
              </div>
              <div>
                <p className="font-bold">Erreur</p>
                <p>{error}</p>
              </div>
            </div>
          </motion.div>
        )}
        
        {success && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-green-100 border-l-4 border-green-500 text-green-700 p-4 mb-4 rounded-md shadow-sm"
          >
            <div className="flex">
              <div className="py-1">
                <CheckIcon className="h-6 w-6 text-green-500 mr-3" />
              </div>
              <div>
                <p className="font-bold">Succès</p>
                <p>{success}</p>
                <p className="mt-2">Redirection vers la page de connexion...</p>
              </div>
            </div>
          </motion.div>
        )}
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm space-y-4">
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Nouveau mot de passe
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                  placeholder="Nouveau mot de passe"
                  value={password}
                  onChange={handlePasswordChange}
                />
                <div className="absolute inset-y-0 right-0 pr-3 flex items-center">
                  <ShieldCheckIcon className={`h-5 w-5 ${passwordStrength >= 3 ? 'text-green-500' : 'text-gray-400'}`} />
                </div>
              </div>
              
              {/* Indicateur de force du mot de passe */}
              {password.length > 0 && (
                <div className="mt-2">
                  <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${getStrengthColor()}`} 
                      style={{ width: `${(passwordStrength / 4) * 100}%` }}
                    ></div>
                  </div>
                  <div className="mt-2 text-xs text-gray-600">
                    <ul className="space-y-1">
                      <li className={passwordCriteria.length ? 'text-green-600' : 'text-gray-500'}>
                        {passwordCriteria.length ? '✓' : '○'} Au moins 8 caractères
                      </li>
                      <li className={passwordCriteria.uppercase ? 'text-green-600' : 'text-gray-500'}>
                        {passwordCriteria.uppercase ? '✓' : '○'} Au moins une majuscule
                      </li>
                      <li className={passwordCriteria.lowercase ? 'text-green-600' : 'text-gray-500'}>
                        {passwordCriteria.lowercase ? '✓' : '○'} Au moins une minuscule
                      </li>
                      <li className={passwordCriteria.number ? 'text-green-600' : 'text-gray-500'}>
                        {passwordCriteria.number ? '✓' : '○'} Au moins un chiffre
                      </li>
                      <li className={passwordCriteria.special ? 'text-green-600' : 'text-gray-500'}>
                        {passwordCriteria.special ? '✓' : '○'} Au moins un caractère spécial
                      </li>
                    </ul>
                  </div>
                </div>
              )}
            </div>
            
            <div>
              <label htmlFor="confirm-password" className="block text-sm font-medium text-gray-700 mb-1">
                Confirmer le mot de passe
              </label>
              <input
                id="confirm-password"
                name="confirmPassword"
                type="password"
                required
                className="appearance-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                placeholder="Confirmer le mot de passe"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
              {password && confirmPassword && (
                <p className={`mt-1 text-xs ${password === confirmPassword ? 'text-green-600' : 'text-red-600'}`}>
                  {password === confirmPassword ? '✓ Les mots de passe correspondent' : '✗ Les mots de passe ne correspondent pas'}
                </p>
              )}
            </div>
          </div>
          
          <div>
            <motion.button
              type="submit"
              disabled={isSubmitting}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-accent focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary transition-colors duration-300 disabled:opacity-50"
            >
              <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                <KeyIcon className="h-5 w-5 text-white" aria-hidden="true" />
              </span>
              {isSubmitting ? 'Réinitialisation en cours...' : 'Réinitialiser le mot de passe'}
            </motion.button>
          </div>
          
          <div className="text-center mt-4">
            <a 
              href="/login" 
              className="text-sm text-primary hover:text-accent transition-colors duration-300"
            >
              Retour à la page de connexion
            </a>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default ResetPassword;